import { hrpFacilityDetails } from '@constants/index';
import CommonFormFields from '@pages/Forms/CommonFormFields';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './HRPFacilityDetails.css';
import AlertPopup from '../../CreateNewChild/AlertPopup';
import { useSelector } from 'react-redux';
import { RootState } from '@store/reducers';
import { getData, getDataWithCache } from '../../../../../services/CommonApiService';
import endpoints from '@config/endpoints';
import dayjs from 'dayjs';
import { errorLogger } from '@utils/applicationLogUtils';
import { ANC_COMMON_CONSTANTS, FacilityDetails as AncConstants } from '@constants/ancConstants';
import BadgesComponent from '@components/common/Badges';
import Button from '@components/common/Button';
import { ancPeriodDateCalculation } from '../../ANC/Forms/CreateAndEdit/AncDateCaluculation';
import FormatDate from '@utils/dateFormat';

interface objectType {
  isError: boolean;
  message: string;
  isDisable: boolean;
  value: any;
  id?: any;
  code?: any;
  ufTaggedId?: any;
}

export interface formStateType {
  [key: string]: objectType;
}
interface HPRFacilityDetailsProps {
  onCancelFacilityDetails: () => void,
  onSubmitFacilityDetails: (data: any) => void,
  onGoingPregnancyData?: any,
};

const HRPFacilityDetails = ({ onCancelFacilityDetails, onSubmitFacilityDetails, onGoingPregnancyData }: HPRFacilityDetailsProps) => {
  const facilityObj: any = {
    facilityType: {
      isError: false,
      message: '',
      isDisable: false,
      value: '',
      id: '',
      code: undefined,
    },
    facilityName: {
      isError: false,
      message: '',
      isDisable: false,
      value: '',
      id: '',
      code: undefined,
    },
    hprName: {
      isError: false,
      message: '',
      isDisable: false,
      value: '',
      ufTaggedId: '',
    },
    ashaName: {
      isError: false,
      message: '',
      isDisable: false,
      value: '',
    },
    dateOfVisit: {
      isError: false,
      message: '',
      isDisable: false,
      value: '',
    },
  };
  const userId = localStorage.getItem('userId');
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [navItemsModal, setnavItemsModal] = useState(['Facility Details']);
  const [selectPerc, setSelectedPerc] = useState<any>(0);
  const [facilityFormState, setFacilityFormState] =
    useState<formStateType>(facilityObj);
  const [facilityData, setFacilityData] = useState<any>(hrpFacilityDetails);
  const [formCompleted, setFormCompleted] = useState<boolean[]>(
    Array(navItemsModal.length).fill(false),
  );
  const [alertModalOpen, setAlertModalOpen] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [facilityDetailsRes, setFacilityDetailsRes] = useState<any>();

  const lmpDate = useSelector((state: RootState) => state.beneficiary.lmpDate);

  const navs = 100 / navItemsModal.length;

  useEffect(() => {
    getFacilityData();
  }, []);

  useEffect(() => {
    const lmpDateToUse = onGoingPregnancyData?.lmp;
    if (!lmpDateToUse || (typeof lmpDateToUse === 'string' && lmpDateToUse.trim() === '')) {
      return;
    }

    try {
      const fourthAncMetaData = ancPeriodDateCalculation(lmpDateToUse, 4);

      const currentDate = dayjs();
      const fourthAncEndDate = dayjs(fourthAncMetaData.endDate);
      
      const daysSinceFourthAncEnd = currentDate.diff(fourthAncEndDate, 'day');
      
      let maxDate = FormatDate(currentDate);
      let isFieldDisabled = false;
      let disabledMessage = '';
      
      if (daysSinceFourthAncEnd >= 0 && daysSinceFourthAncEnd < 15) {
        const fourthAncEndFormatted = FormatDate(fourthAncEndDate);
        const currentDateFormatted = FormatDate(currentDate);
        maxDate = dayjs(fourthAncEndFormatted).isAfter(dayjs(currentDateFormatted)) 
          ? currentDateFormatted 
          : fourthAncEndFormatted;
      } else if (daysSinceFourthAncEnd >= 15) {
        isFieldDisabled = true;
        disabledMessage = AncConstants.VISIT_DATE_BEYOND_30_DAYS_ERROR(true);
      }
    
      setFacilityData((prevState: any) => ({
        ...prevState,
        ['dateOfVisit']: {
          ...prevState['dateOfVisit'],
          '#maxDate': maxDate,
          '#isDisabled': isFieldDisabled,
        }
      }));
      setFacilityFormState((prevState: any) => ({
        ...prevState,
        ['dateOfVisit']: {
          ...prevState['dateOfVisit'],
          'isError': isFieldDisabled,
          'message': disabledMessage,
        }
      }));
    } catch (error) {
      errorLogger(error);
    }
  }, [onGoingPregnancyData?.lmp]);

  const getFacilityData = async () => {
    try {
      const facilityDetailsRespone = await getDataWithCache<any>(`${endpoints.getLinkedFacilitiesByUserById}/${userId}`);
      setFacilityDetailsRes(facilityDetailsRespone?.data[0]);
      const parentFacility = facilityDetailsRespone.data[0]?.find((item: any) => item.is_parent === true,);
      const facilityTypeOptions = facilityDetailsRespone?.data[0]?.map((key: any) => ({
        value: key.facility_type_name,
        label: key.facility_type_name,
        id: key.facility_type_id,
        code: key.facility_type_code,
      }),
      );
      const facilityNameOptions = facilityDetailsRespone?.data[0]?.map((key: any) => ({
        value: key.facility_name,
        label: key.facility_name,
        id: key.facility_id,
        code: key.facility_code,
      }))

      setFacilityFormState((prevState: any) => ({
        ...prevState,
        facilityName: {
          ...prevState.facilityName,
          value: parentFacility?.facility_name,
          id: parentFacility?.facility_id,
        },
        facilityType: {
          ...prevState.facilityType,
          value: parentFacility?.facility_type_name,
          id: parentFacility?.facility_type_id,
        },
      }));
      setFacilityData((prevState: any) => {
        return {
          ...prevState,
          facilityType: {
            ...prevState.facilityType,
            '#options': facilityTypeOptions,
          },
          facilityName: {
            ...prevState.facilityName,
            '#options': facilityNameOptions,
          },
        };
      });
      fetchHprName(parentFacility?.facility_id);
    } catch (error: any) {
      errorLogger(error?.response?.data || error?.response || error)
    }
  }

  const fetchHprName = async (id: any) => {
    if (id) {
      try {
        const Health_provider = await getDataWithCache<any>(`${endpoints.getHealthProviderTagged(id)}`);
        const healthProviderOptions = Health_provider?.data?.map((key: any) => ({
          value: `${key?.f_name} ${key?.l_name}`,
          label: `${key?.f_name} ${key?.l_name}`,
          id: key?.user_id,
          code: key?.user_code,
          ufTaggedId: key?.uf_tagged_id,
        }));
        setFacilityData((prevState: any) => {
          return {
            ...prevState,
            hprName: {
              ...prevState.hprName,
              '#options': healthProviderOptions,
            },
          };
        });
      } catch (error: any) {
        errorLogger(error?.response?.data || error?.response || error)
      }
    }
  };

  const handleFacilityFormChange = (value: any, key: string, id: any, option: any, code: any, changedData: any) => {

    if (key === 'facilityName' && !value) {
      setFacilityFormState((prevState: any) => ({
        ...prevState,
        facilityType: {
          ...prevState.facilityType,
          value: '',
        },
        hprName: {
          ...prevState.hprName,
          value: '',
        }
      }));
    }
    if (key === 'facilityType' && !value) {
      setFacilityFormState((prevState: any) => ({
        ...prevState,
        isError: false,
        message: '',
        facilityName: {
          ...prevState.facilityName,
          value: '',
        },
        hprName: {
          ...prevState.hprName,
          value: '',
        }
      }));
    }
    if (key === 'facilityName' && value) {
      const facilityDetail = facilityDetailsRes?.find((item: any) => item.facility_name === value);
      fetchHprName(facilityDetail.facility_id);
      setFacilityFormState((prevState: any) => ({
        ...prevState,
        facilityType: {
          ...prevState.facilityType,
          isError: false,
          message: '',
          value: facilityDetail.facility_type_name,
          label: facilityDetail.facility_type_name,
          id: facilityDetail.facility_type_id,
          code: facilityDetail.facility_type_code,
        }
      }))
    }

    if (key === 'facilityType' && value) {
      const facilityDetail = facilityDetailsRes?.find((item: any) => item.facility_type_name === value);
      setFacilityFormState((prevState: any) => ({
        ...prevState,
        facilityName: {
          ...prevState.facilityName,
          value: facilityDetail.facility_name,
          label: facilityDetail.facility_name,
          id: facilityDetail.facility_id,
          code: facilityDetail.facility_code,
        }
      }))
    }

    if (key === "hprName") {
      setFacilityFormState((prevState: any) => {
        return {
          ...prevState,
          ashaName: {
            ...prevState.ashaName,
            value: '',
          },
        };
      });
      if (value) {
        fetchAshaName(changedData?.ufTaggedId);
      } else {
        setFacilityData((prevState: any) => {
          return {
            ...prevState,
            ashaName: {
              ...prevState.ashaName,
              '#options': [],
            },
          };
        });
      }
    }
    setFacilityFormState((prevState: any) => ({
      ...prevState,
      [key]: {
        ...prevState[key],
        value: value,
        id: id,
        code: code,
        isError: false,
        message: '',
      },
    }));
  };

  const fetchAshaName = async (ufTaggedId: any) => {
    try {
      const ashaNameRes = await getDataWithCache<any>(`${endpoints.getMobiliserTagged(ufTaggedId)}`);
      const ashanameOptions = ashaNameRes?.data.map((key: any) => ({
        value: key?.f_name + ' ' + key?.l_name,
        label: key?.f_name + ' ' + key?.l_name,
        id: key?.mobiliser_id,
        code: key?.user_code,
      }));
      setFacilityData((prevState: any) => {
        return {
          ...prevState,
          ashaName: {
            ...prevState.ashaName,
            '#options': ashanameOptions,
          },
        };
      });
    } catch (error: any) {
      errorLogger(error?.response?.data || error?.response || error)
    }
  }

  const finishHandler = async () => {
    let checkError = false;

    if (!facilityFormState.facilityType.value) {
      setFacilityFormState((prevState: any) => ({
        ...prevState,
        facilityType: {
          ...prevState.facilityType,
          isError: true,
          message: AncConstants.facilityTypeError,
        },
      }));
      checkError = true;
    }
    if (!facilityFormState.facilityName.value) {
      setFacilityFormState((prevState: any) => ({
        ...prevState,
        facilityName: {
          ...prevState.facilityName,
          isError: true,
          message: AncConstants.facilityNameError,
        },
      }));
      checkError = true;
    }
    if (!facilityFormState.hprName.value) {
      setFacilityFormState((prevState: any) => ({
        ...prevState,
        hprName: {
          ...prevState.hprName,
          isError: true,
          message: AncConstants.healthProviderName,
        },
      }));
      checkError = true;
    }
    if (facilityFormState.dateOfVisit.isError && facilityFormState.dateOfVisit.message) {
      checkError = true;
    } else if (!facilityFormState.dateOfVisit.value) {
      setFacilityFormState((prevState: any) => ({
        ...prevState,
        dateOfVisit: {
          ...prevState.dateOfVisit,
          isError: true,
          message: AncConstants.visitDateError,
        },
      }));
      checkError = true;
    }
    if (!checkError) {
      const facilityDetailsSubmitted = {
        facilityId: facilityFormState.facilityName.id,
        healthProviderId: facilityFormState.hprName.id,
        mobilizerId: facilityFormState.ashaName.id,
        visitDate: dayjs(facilityFormState.dateOfVisit.value).format('YYYY-MM-DD')
      }
      onSubmitFacilityDetails(facilityDetailsSubmitted);
    }

    if (!checkError) {
      const newFormCompleted = [...formCompleted];
      newFormCompleted[activeIndex] = true;
      setFormCompleted(newFormCompleted);
      const perc = (activeIndex + 1) * navs;
      setSelectedPerc(perc);
    } else {
      const newFormCompleted = [...formCompleted];
      newFormCompleted[activeIndex] = false;
      setFormCompleted(newFormCompleted);
      setSelectedPerc(0);
      return;
    }
  };

  const closeAlertModal = (value: boolean) => {
    if (value) {
      setAlertModalOpen(false);
      onCancelFacilityDetails();
    } else {
      setAlertModalOpen(false);
    }
  };
  const onClickCancelFacilityDetails = () => {
    setModalTitle('Do you want to close the form?');
    setAlertModalOpen(true);

  }
  const onClickSubmitFacilityDetails = () => {
    finishHandler()
  }

  return (
    <>
      <div className='hrp_facility_details'>
        <div>
          <BadgesComponent
            className="over-badge"
            badgeName="Facility Details"
            variant="primary-1"
            badgeType="case5"
          />
        </div>
        <div key="Service">
          <form className="general_details_hrp_facility_details">
            <CommonFormFields
              formData={facilityData}
              formState={facilityFormState}
              handleFormChange={handleFacilityFormChange}
            />
          </form>
        </div>
        <AlertPopup
          showModal={alertModalOpen}
          closeModal={closeAlertModal}
          title={modalTitle}
        />
        <div className='facility_details_hrp_buttons'>
          <Button onClick={onClickCancelFacilityDetails} buttonName={'Cancel'} colorType={'Secondary'} id={''} />
          <Button onClick={onClickSubmitFacilityDetails} buttonName={'Submit'} colorType={'primary'} id={''} />
        </div>
      </div>
    </>
  );
};

export default HRPFacilityDetails;
